import { useEffect, useRef, useState } from 'react'

type Callback = () => void

const callbacks = new WeakMap<Element, Callback>()
let sharedObserver: IntersectionObserver | null = null

/** ONE IntersectionObserver is created for the whole page and reused. */
function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null
  if (sharedObserver) return sharedObserver

  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const cb = callbacks.get(entry.target)
        callbacks.delete(entry.target)
        sharedObserver?.unobserve(entry.target)
        cb?.()
      }
    },
    { rootMargin: '0px 0px -40px 0px', threshold: 0.1 },
  )

  return sharedObserver
}

/**
 * Scroll reveal state driven by the single shared observer.
 * Returns the ref to attach and whether the element has entered the viewport.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = getObserver()
    if (!observer) {
      setShown(true)
      return
    }

    callbacks.set(el, () => setShown(true))
    observer.observe(el)

    return () => {
      callbacks.delete(el)
      observer.unobserve(el)
    }
  }, [])

  return { ref, shown }
}
